// Drives the voice from the controls. The throttle and the ignition live in the
// view state; the starter is a held button, so it lives here and never in a link.
//
// A voice that accepts throttle, ignition and starter (the wasm voice) gets them
// as controls. The mock accepts only rpm, so the stand-in model turns the controls
// into an rpm, stepped on a timer while the audio runs.

import type { ViewStore } from "../../state/store";
import type { AudioSession } from "./audio-session";
import { STOPPED, step, warmStart, type StandInInputs, type StandInState } from "./powertrain-stand-in";

/** Tick of the stand-in model, in ms. A timer rather than rAF, so a hidden tab keeps its sound right. */
const TICK_MS = 20;
/** The smallest rpm change worth sending. */
const RPM_EPSILON = 0.5;

export type EnginePhase = "stopped" | "cranking" | "running";

export class PowertrainDriver {
  private readonly store: ViewStore;
  private readonly session: AudioSession;
  private starterHeld = false;
  private model: StandInState = { ...STOPPED };
  private sentRpm = Number.NaN;
  private lastTick = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly listeners = new Set<() => void>();
  private readonly unsubscribe: (() => void)[] = [];

  constructor(store: ViewStore, session: AudioSession) {
    this.store = store;
    this.session = session;
    this.unsubscribe.push(
      session.onStatus((status) => (status === "on" ? this.begin() : this.end())),
      store.subscribe((s, prev) => {
        if (s.controls.throttle !== prev.controls.throttle || s.controls.ignition !== prev.controls.ignition) {
          this.sendDirect();
          this.emit();
        }
      }),
    );
    // The mock takes its first speed from init, not from the rpm AudioParam.
    session.startParams = () => ({ rpm: warmStart(this.inputs()).rpm });
    if (session.status === "on") this.begin();
  }

  /** Whether the voice takes the controls itself rather than through the stand-in. */
  get direct(): boolean {
    return this.session.accepts("throttle");
  }

  get starter(): boolean {
    return this.starterHeld;
  }

  /** What the engine is doing, from the stand-in (or from the controls for a direct voice). */
  get phase(): EnginePhase {
    if (this.session.status !== "on") return "stopped";
    if (this.direct) return this.starterHeld ? "cranking" : this.store.state.controls.ignition ? "running" : "stopped";
    if (this.model.running) return "running";
    return this.starterHeld ? "cranking" : "stopped";
  }

  setStarter(held: boolean): void {
    if (held === this.starterHeld) return;
    this.starterHeld = held;
    this.sendDirect();
    this.emit();
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  dispose(): void {
    this.end();
    for (const u of this.unsubscribe) u();
  }

  private inputs(): StandInInputs {
    const c = this.store.state.controls;
    return { throttle: c.throttle, ignition: c.ignition, starter: this.starterHeld };
  }

  private begin(): void {
    // A link that opens with the ignition on opens a running engine.
    this.model = warmStart(this.inputs());
    this.sentRpm = Number.NaN;
    this.lastTick = performance.now();
    this.sendDirect();
    this.sendRpm();
    this.timer ??= setInterval(() => this.tick(), TICK_MS);
    this.emit();
  }

  private end(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    this.model = { ...STOPPED };
    this.emit();
  }

  private tick(): void {
    const now = performance.now();
    const dt = Math.min(0.25, (now - this.lastTick) / 1000); // a throttled timer must not jump the model
    this.lastTick = now;
    if (this.direct) return;
    const wasRunning = this.model.running;
    this.model = step(this.model, this.inputs(), dt);
    this.sendRpm();
    if (this.model.running !== wasRunning) this.emit();
  }

  private sendRpm(): void {
    if (this.direct || this.session.status !== "on") return;
    const rpm = this.model.rpm;
    if (Math.abs(rpm - this.sentRpm) < RPM_EPSILON && !(rpm === 0 && this.sentRpm !== 0)) return;
    this.session.control("rpm", rpm);
    this.sentRpm = rpm;
  }

  private sendDirect(): void {
    if (!this.direct) return;
    const i = this.inputs();
    this.session.control("throttle", i.throttle);
    this.session.control("ignition", i.ignition ? 1 : 0);
    if (this.session.accepts("starter")) this.session.control("starter", i.starter ? 1 : 0);
  }

  private emit(): void {
    for (const l of [...this.listeners]) l();
  }
}
